/**
 * Embed entry point. Built as an IIFE that assigns `window.AgenticChat`.
 *
 *   <script src="/agentic-chat.js"></script>
 *   <script>AgenticChat.init({ branding: { name: 'CAIRA' } })</script>
 *
 * Or with zero JS, via data attributes on the script tag:
 *   <script src="/agentic-chat.js" data-auto data-widget-key="pk_live_…" data-name="CAIRA"></script>
 *
 * Messages and Home screens behind the chat's back arrow (the sales widget on
 * a website): `AgenticChat.init({ home: { cards: [...] } })`, or
 * `data-home='{"cards":[...]}'` on the script tag (`data-home=""` for the
 * defaults). See HomeConfig.
 *
 * Inside the client's own app, where the user is signed in, add
 * `data-identity-url="/api/trinity/identity-token"`: the widget fetches the
 * token the app's server signs for that user (with the page's cookies) and the
 * chat knows who they are. Or pass `api: { kind: 'agent', identity: { getToken } }`.
 *
 * `data-widget-key` is the tenant's PUBLIC widget key from the Trinity admin
 * portal: the widget trades it for a short visitor token, valid only on the
 * tenant's allowed domains and only for chat. Host pages never carry the
 * tenant's API key — it does not belong in a browser.
 *
 * Without a key on the page, the key this bundle was built with is used
 * (VITE_WIDGET_KEY), so `AgenticChat.init({ api: { kind: 'agent', baseUrl } })`
 * keeps working as it always did.
 */
import { mount, type ChatInstance, type MountOptions, type OpenOptions } from './mount';
import { MockTransport } from '@/transport/mock/mockTransport';
import { RestTransport, type RestTransportOptions } from '@/transport/rest/restTransport';
import { WsTransport, type WsTransportOptions } from '@/transport/ws/wsTransport';
import { AgentApiTransport, type AgentApiTransportOptions } from '@/transport/api/agentApiTransport';
import { DEFAULT_AVATAR_PATH, DEFAULT_LAUNCHER_ICON_PATH, type GreetingConfig, type HomeConfig } from '@/core/config';

/** The widget host's own tenant key, inlined at build time; public by design. */
const BUILT_IN_WIDGET_KEY: string | undefined = import.meta.env.VITE_WIDGET_KEY || undefined;

/** Only set while this script is executing, so capture it now. */
const SCRIPT_SRC = (document.currentScript as HTMLScriptElement | null)?.src;

/**
 * Bundled defaults like the avatar live beside the widget script, not on the
 * host website — so resolve them against the script's own URL.
 */
function assetUrl(path: string): string {
  return SCRIPT_SRC ? new URL(path, SCRIPT_SRC).href : path;
}

/**
 * The private "Ask your business" chat: the signed-in person's questions about
 * their own business data, which only they see — never the support team.
 *
 * The same widget, opened by the host app (a header button) with
 * `chat.open({ mode: 'data' })`: its own conversation, its own panel (wider,
 * marked private), no launcher of its own. Needs `api.kind: 'agent'` with a
 * signed `identity` whose token grants data access (`ext.data`); the server
 * refuses a private chat to anyone else.
 */
export interface DataModeOptions {
  /** Header title. Default "Ask your business". */
  title?: string;
  /** Under the title. Default "Private · only you can see this". */
  statusLabel?: string;
  /** The welcome line and starter questions. */
  greeting?: GreetingConfig;
}

export const DEFAULT_DATA_GREETING: GreetingConfig = {
  text:
    'Ask me about your business — sales, receivables, purchases, stock.\n\n' +
    'Answers come from your company’s own data, and only you can see this chat.',
  suggestions: ['Sales this month', 'Top 10 customers by sales', 'Outstanding receivables', 'Sales by month this year'],
};

export interface InitOptions extends MountOptions {
  /** Set up the private data chat (see DataModeOptions). Agent API only. */
  dataMode?: DataModeOptions;
  /**
   * Convenience shorthand so host pages never import transport classes.
   * Omit entirely to get the mock.
   */
  api?:
    | ({ kind: 'agent' } & AgentApiTransportOptions)
    | ({ kind: 'rest' } & RestTransportOptions)
    | ({ kind: 'websocket' } & WsTransportOptions)
    | { kind: 'mock'; streaming?: boolean; errorRate?: number };
}

const instances = new Set<ChatInstance>();

function init(options: InitOptions = {}): ChatInstance {
  const { api, ...rest } = options;
  const branding = {
    ...rest.branding,
    // `??`, not a spread default: '' must still mean "no image".
    avatarUrl: rest.branding?.avatarUrl ?? assetUrl(DEFAULT_AVATAR_PATH),
  };
  const launcher = {
    ...rest.launcher,
    iconUrl: rest.launcher?.iconUrl ?? assetUrl(DEFAULT_LAUNCHER_ICON_PATH),
  };
  const { dataMode, ...base } = rest as typeof rest & { dataMode?: DataModeOptions };
  const dataAvailable = !!dataMode && api?.kind === 'agent';
  if (dataMode && !dataAvailable) {
    console.warn('[agentic-chat] dataMode needs api.kind "agent"; the data chat is not set up.');
  }

  // Both panels are reached through `wrapper`, so either can hand over to the other.
  let wrapper: ChatInstance;
  let data: ChatInstance | undefined;

  const main = mount({
    ...base,
    branding,
    launcher,
    transport: base.transport ?? createTransport(api),
    ...(dataAvailable ? { onOpenDataMode: () => wrapper.open({ mode: 'data' }) } : {}),
  });

  /** The data chat, mounted the first time it is opened. */
  const dataChat = (): ChatInstance => {
    if (data) return data;
    const agent = api as { kind: 'agent' } & AgentApiTransportOptions;
    data = mount({
      ...base,
      mode: 'data',
      storageNamespace: 'agentic-chat:data',
      branding: {
        ...branding,
        name: dataMode?.title ?? 'Ask your business',
        tagline: undefined,
        statusLabel: dataMode?.statusLabel ?? 'Private · only you can see this',
      },
      // Opened by the host app only; never a second chat button on the page.
      launcher: { ...launcher, enabled: false },
      // The sales screens have no place in a private chat.
      home: undefined,
      greeting: dataMode?.greeting ?? DEFAULT_DATA_GREETING,
      openOnLoad: false,
      // Nobody from the team joins a private chat.
      features: { ...base.features, handoff: false },
      transport: new AgentApiTransport({
        ...agent,
        widgetKey: agent.widgetKey || BUILT_IN_WIDGET_KEY,
        mode: 'data',
        bootstrapMessage: undefined,
      }),
      onExitDataMode: () => wrapper.open({ mode: 'chat' }),
    });
    return data;
  };

  wrapper = {
    ...main,
    engine: main.engine,
    logger: main.logger,
    open(options?: OpenOptions) {
      if (options?.mode === 'data') {
        if (!dataAvailable) {
          main.logger.warn('open({ mode: "data" }) without dataMode set up; opening the chat instead');
          main.open(options);
          return;
        }
        main.close();
        dataChat().open();
        return;
      }
      data?.close();
      main.open(options);
    },
    toggle() {
      if (data?.engine.store.getState().isOpen) {
        data.close();
        return;
      }
      main.toggle();
    },
    close() {
      main.close();
      data?.close();
    },
    // Signing out forgets the private chat too: it was this person's alone.
    logout() {
      return Promise.all([main.logout(), data?.logout()]).then(() => undefined);
    },
    destroy() {
      main.destroy();
      data?.destroy();
      instances.delete(wrapper);
    },
  };
  instances.add(wrapper);
  return wrapper;
}

function createTransport(api: InitOptions['api']) {
  if (!api || api.kind === 'mock') {
    return new MockTransport({ streaming: api?.streaming ?? true, errorRate: api?.errorRate ?? 0 });
  }
  if (api.kind === 'agent') {
    // A page that passes no key gets the one this bundle was built with
    // (VITE_WIDGET_KEY, from the widget host's .env.prod). That keeps embeds
    // written before widget keys existed — `api: { kind: 'agent', baseUrl }` —
    // working unchanged. A page serving another tenant passes its own key.
    return new AgentApiTransport({ ...api, widgetKey: api.widgetKey || BUILT_IN_WIDGET_KEY });
  }
  if (api.kind === 'rest') return new RestTransport(api);
  return new WsTransport(api);
}

/**
 * `data-identity-url`: GET the host app's own endpoint, with its cookies, and
 * read `{ token }`. Null on any failure: the chat then carries on anonymously
 * where the tenant allows it.
 */
async function fetchIdentityToken(url: string): Promise<string | null> {
  const response = await fetch(url, { credentials: 'include', headers: { accept: 'application/json' } });
  if (!response.ok) return null;
  const body = (await response.json()) as { token?: unknown };
  return typeof body.token === 'string' ? body.token : null;
}

function destroyAll(): void {
  instances.forEach((instance) => instance.destroy());
  instances.clear();
}

/**
 * `data-home`: the Messages and Home screens as JSON (see HomeConfig), or
 * present-but-empty for the defaults. Bad JSON leaves it off, with a warning.
 */
function parseHome(value: string | undefined): HomeConfig | undefined {
  if (value === undefined) return undefined;
  if (!value.trim()) return {};
  try {
    return JSON.parse(value) as HomeConfig;
  } catch {
    console.warn('[agentic-chat] data-home is not valid JSON; Messages and Home are off.');
    return undefined;
  }
}

/** `data-auto` on the script tag mounts without any inline JS. */
function autoInit(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script || !('auto' in script.dataset)) return;
  const d = script.dataset;
  init({
    branding: d.name ? { name: d.name } : undefined,
    home: parseHome(d.home),
    theme: { ...(d.primary ? { primary: d.primary } : {}), ...(d.scheme ? { colorScheme: d.scheme as 'light' | 'dark' | 'auto' } : {}) },
    launcher: {
      ...(d.position ? { position: d.position as 'bottom-right' | 'bottom-left' } : {}),
      // Present-but-empty (`data-launcher-icon=""`) means the built-in icon.
      ...(d.launcherIcon !== undefined ? { iconUrl: d.launcherIcon } : {}),
    },
    // `data-widget-key` (or `data-kind="agent"`) selects the Agent API. The API
    // defaults to the origin this script was served from.
    ...(d.widgetKey || (d.apiUrl && d.kind === 'agent')
      ? {
          api: {
            kind: 'agent' as const,
            baseUrl: d.apiUrl ?? (SCRIPT_SRC ? new URL(SCRIPT_SRC).origin : ''),
            ...(d.widgetKey ? { widgetKey: d.widgetKey } : {}),
            ...(d.bootstrap ? { bootstrapMessage: d.bootstrap } : {}),
            ...(d.identityUrl ? { identity: { getToken: () => fetchIdentityToken(d.identityUrl as string) } } : {}),
          },
        }
      : d.apiUrl
        ? { api: { kind: 'rest' as const, baseUrl: d.apiUrl, stream: d.stream === 'true' } }
        : {}),
  });
}

/* Named exports become properties of `window.AgenticChat` in the IIFE build,
   so the host page calls `AgenticChat.init({...})`. */
export const version = '0.1.0';
export { init, destroyAll };
export type { ChatInstance, MountOptions };

autoInit();
